import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import UserAvatar from './UserAvatar';
import SecondaryLoader from './SecondaryLoader';
import { color_shades_dark, color_defaults_white, voicify_spark_light, voicify_spark_lightest } from '../../constants/colors';

export interface AvatarUploadProps {
    name: string;
    currentImageUri?: string | null;
    imageFile?: File;
    size?: 'small' | 'large';
    label?: string;
    showHint?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    onChange: (file: File) => void;
    onError?: (errors: string[]) => void;
    onClearError?: () => void;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
    name, currentImageUri, imageFile, size = 'small', label, showHint = false,
    isLoading = false, disabled = false, onChange, onError, onClearError
}) => {
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        setPreviewUrl(null);
        if (!imageFile) return;
        const reader = new FileReader();
        reader.onload = () => setPreviewUrl(reader.result as string);
        reader.readAsDataURL(imageFile);
        return () => {
            reader.onload = null;
            if (reader.readyState === 1) reader.abort();
        };
    }, [imageFile, currentImageUri]);

    const avatarSize = size === 'large' ? 175 : 120;
    const wrapperSize = size === 'large' ? 215 : 140;
    const ringSize = size === 'large' ? 195 : 135;
    const innerRingSize = size === 'large' ? 183 : 126;
    const overlayIconSize = size === 'large' ? 32 : 24;
    const overlayTextSize = size === 'large' ? 12 : 11;
    const initials = (name || 'App').split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 3);

    const handleAvatarClick = () => {
        if (!disabled && !isLoading) avatarInputRef.current?.click();
    };

    const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || disabled || isLoading) return;

        let message = '';
        if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
            message = 'Invalid file type. Please select a JPG, PNG, GIF, or WebP image.';
        } else if (file.size > 5 * 1024 * 1024) {
            message = `Image is too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Please select an image under 5MB.`;
        }
        setError(message);
        if (message) {
            onError?.([message]);
            return;
        }
        onClearError?.();
        onChange(file);
    };

    return (
        <AvatarSection>
            {label && <AvatarLabel>{label}</AvatarLabel>}
            <input
                type="file"
                ref={avatarInputRef}
                onChange={handleAvatarFileChange}
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                disabled={disabled || isLoading}
            />
            {isLoading ? (
                <AvatarLoaderWrapper
                    wrapperSize={wrapperSize}
                >
                    <AvatarRing ringSize={ringSize} />
                    <AvatarInnerRing innerRingSize={innerRingSize} />
                    <AvatarLoaderContent avatarSize={avatarSize}>
                        <SecondaryLoader className="grey" />
                    </AvatarLoaderContent>
                </AvatarLoaderWrapper>
            ) : (
                <ClickableAvatarWrapper
                    onClick={handleAvatarClick}
                    type="button"
                    aria-label={label || 'Change avatar'}
                    title={disabled ? undefined : 'Click to change avatar'}
                    wrapperSize={wrapperSize}
                    avatarSize={avatarSize}
                    disabled={disabled || isLoading}
                >
                    <AvatarRing ringSize={ringSize} />
                    <AvatarInnerRing innerRingSize={innerRingSize} />
                    <UserAvatar
                        firstName={name || 'A'}
                        lastName=""
                        src={previewUrl || currentImageUri}
                        size={avatarSize}
                        noShadow={true}
                        noImageText={initials}
                    />
                    {!disabled && (
                        <AvatarUploadOverlay avatarSize={avatarSize}>
                            <AvatarUploadIcon viewBox="0 0 24 24" iconSize={overlayIconSize}>
                                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                            </AvatarUploadIcon>
                            <AvatarUploadText fontSize={overlayTextSize}>Change</AvatarUploadText>
                        </AvatarUploadOverlay>
                    )}
                </ClickableAvatarWrapper>
            )}
            {showHint && <AvatarHint>Click to upload a new avatar (.png | .jpeg | .jpg | .gif | .webp | max 5MB)</AvatarHint>}
            {error && <AvatarError role="alert">{error}</AvatarError>}
        </AvatarSection>
    );
};

const AvatarSection = styled.div``;

const AvatarLabel = styled.label`
    display: block;
    font-family: Muli;
    font-size: 14px;
    text-align: left;
    margin: 0 16px 12px 4px;
    font-weight: 600;
`;

const AvatarHint = styled.p`
    font-size: 12px;
    color: ${color_shades_dark};
    margin-top: 8px;
    margin-bottom: 0;
`;

const AvatarLoaderWrapper = styled.div<{ wrapperSize: number }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.wrapperSize}px;
    height: ${props => props.wrapperSize}px;
    cursor: pointer;
`;

const AvatarLoaderContent = styled.div<{ avatarSize: number }>`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.avatarSize}px;
    height: ${props => props.avatarSize}px;
    border-radius: 50%;
    background: ${color_defaults_white};
    z-index: 2;
`;

const ClickableAvatarWrapper = styled.button<{ wrapperSize: number; avatarSize: number; disabled?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.wrapperSize}px;
    height: ${props => props.wrapperSize}px;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.5 : 1};
    
    padding: 0;
    border: none;
    background: transparent;

    /* White background circle to ensure logos with transparent backgrounds look good */
    &::before {
        content: '';
        position: absolute;
        width: ${props => props.avatarSize}px;
        height: ${props => props.avatarSize}px;
        border-radius: 50%;
        background: ${color_defaults_white};
        z-index: 1;
    }
    
    /* Ensure the avatar is above the background */
    > *:not(:first-of-type):not(:nth-of-type(2)) {
        z-index: 2;
    }

    &:hover, &:focus {
        > div:last-of-type {
            opacity: 1;
        }
    }
`;

const AvatarRing = styled.div<{ ringSize: number }>`
    position: absolute;
    width: ${props => props.ringSize}px;
    height: ${props => props.ringSize}px;
    border-radius: 50%;
    background: ${voicify_spark_lightest};
    opacity: 0.6;
`;

const AvatarInnerRing = styled.div<{ innerRingSize: number }>`
    position: absolute;
    width: ${props => props.innerRingSize}px;
    height: ${props => props.innerRingSize}px;
    border-radius: 50%;
    border: 2px solid ${voicify_spark_light};
    background: transparent;
`;

const AvatarUploadOverlay = styled.div<{ avatarSize: number }>`
    position: absolute;
    width: ${props => props.avatarSize}px;
    height: ${props => props.avatarSize}px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 10;
    pointer-events: none;
`;

const AvatarUploadIcon = styled.svg<{ iconSize: number }>`
    width: ${props => props.iconSize}px;
    height: ${props => props.iconSize}px;
    fill: ${color_defaults_white};
    margin-bottom: 4px;
`;

const AvatarUploadText = styled.span<{ fontSize: number }>`
    color: ${color_defaults_white};
    font-size: ${props => props.fontSize}px;
    font-weight: 600;
`;

const AvatarError = styled.p`
    color: #c53030;
    font-size: 12px;
`;

export default AvatarUpload;
